import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { conciergeReviewAPI, errMsg, fmtDate, usd } from '../services/intelligenceApi';
import { GroundingBadge } from './IntelligenceConcierge';
import './Intelligence.css';

/* INTELLIGENCE → CONCIERGE REVIEW → one turn (brain v2 PLAN-CONCIERGE-FACTORY §4.3, §4.7, §6.3, §9.2).
 *
 * The redacted turn as logged (question, answer with its fact spans, facts, tools, grounding, plan mode,
 * versions), the member's feedback, other annotators' verdicts, and the annotation form. The corrected
 * answer is written in placeholder form ({{f:r1.f2.outcome}}), never with typed facts, and must pass the
 * concierge's own grounding gate (checked live, and again by apbe on save). */

/** Answer text cut at valid, non-overlapping spans; each piece is a text node. */
function spanSegments(text, spans) {
  const list = (Array.isArray(spans) ? spans : [])
    .filter((s) => s && Number.isInteger(s.start) && Number.isInteger(s.end) && s.start >= 0 && s.end > s.start && s.end <= text.length)
    .sort((a, b) => a.start - b.start);
  const out = [];
  let at = 0;
  for (const s of list) {
    if (s.start < at) return null; // overlapping: show plain text
    if (s.start > at) out.push({ a: at, b: s.start });
    out.push({ a: s.start, b: s.end, ref: s.ref });
    at = s.end;
  }
  if (at < text.length) out.push({ a: at, b: text.length });
  return out;
}

function Answer({ text, spans }) {
  if (!text) return <p className="in-muted">No answer text (the turn ended without one).</p>;
  const segs = spanSegments(text, spans);
  if (!segs) return <><div className="in-answer">{text}</div><p className="in-muted">Spans overlap; shown without them.</p></>;
  return (
    <div className="in-answer">
      {segs.map((s) => (s.ref
        ? <mark key={s.a} title={s.ref}>{text.slice(s.a, s.b)}<sup>{s.ref}</sup></mark>
        : <React.Fragment key={s.a}>{text.slice(s.a, s.b)}</React.Fragment>))}
    </div>
  );
}

function GateResult({ gate }) {
  if (!gate) return null;
  return (
    <div className={`in-banner ${gate.ok ? 'info' : 'bad'}`} style={{ marginTop: 8 }}>
      {gate.ok ? 'Passes the grounding gate.' : 'Does not pass the grounding gate:'}
      {(gate.violations || []).length > 0 && (
        <ul className="in-violations">
          {gate.violations.map((v, i) => <li key={i}>{v.sentence != null ? `Sentence ${v.sentence + 1}: ` : ''}<b>{v.check}</b> {v.detail}</li>)}
        </ul>
      )}
      {(gate.warnings || []).length > 0 && <ul className="in-warnings">{gate.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
      {(gate.checked || []).length > 0 && (
        <div className="in-muted" style={{ marginTop: 4 }}>Checked {gate.checked.join(', ')}{(gate.not_checked || []).length ? `; not checkable from the log: ${gate.not_checked.join(', ')}` : ''}.</div>
      )}
    </div>
  );
}

function AnnotationForm({ turn, facts, vocab, mine, onSaved }) {
  const [verdict, setVerdict] = useState(mine ? mine.verdict : '');
  const [labels, setLabels] = useState(mine ? mine.labels || [] : []);
  const [corrected, setCorrected] = useState(mine ? mine.corrected_answer || '' : '');
  const [kbGap, setKbGap] = useState(mine ? mine.kb_gap_note || '' : '');
  const [evalCandidate, setEvalCandidate] = useState(mine ? !!Number(mine.eval_candidate) : false);
  const [ref, setRef] = useState(facts[0] ? facts[0].ref : '');
  const [slot, setSlot] = useState('outcome');
  const [gate, setGate] = useState(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const area = useRef(null);
  const canEval = turn.viewer_class === 'member' && Number(turn.training_consent) === 1;

  // live gate check, debounced; apbe checks again on save
  useEffect(() => {
    if (!corrected.trim()) { setGate(null); return undefined; }
    let live = true;
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const r = await conciergeReviewAPI.validate({ turn_id: turn.id, corrected_answer: corrected });
        if (live) setGate(r.data.gate);
      } catch (e) {
        if (live) setGate({ ok: false, violations: [{ sentence: null, check: 'error', detail: errMsg(e) }], warnings: [] });
      } finally {
        if (live) setChecking(false);
      }
    }, 600);
    return () => { live = false; clearTimeout(t); };
  }, [corrected, turn.id]);

  const insert = () => {
    if (!ref || !slot) return;
    const token = `{{f:${ref}.${slot}}}`;
    const el = area.current;
    const at = el ? el.selectionStart : corrected.length;
    const end = el ? el.selectionEnd : corrected.length;
    const next = corrected.slice(0, at) + token + corrected.slice(end);
    setCorrected(next);
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.selectionStart = at + token.length;
        el.selectionEnd = at + token.length;
      }
    });
  };
  const toggleLabel = (l) => setLabels((ls) => (ls.includes(l) ? ls.filter((x) => x !== l) : [...ls, l]));

  const save = async (e) => {
    e.preventDefault();
    if (!verdict) { setMsg({ bad: true, text: 'Pick a verdict.' }); return; }
    setBusy(true);
    setMsg(null);
    try {
      const r = await conciergeReviewAPI.annotate(turn.id, {
        verdict, labels, corrected_answer: corrected.trim() || null, kb_gap_note: kbGap.trim() || null, eval_candidate: canEval && evalCandidate,
      });
      const a = r.data.annotation;
      onSaved(`Saved${a.kbf_task_id ? ` · KB factory task ${a.kbf_task_id} is open for the gap` : ''}.`);
    } catch (err) {
      const g = err && err.response && err.response.data && err.response.data.gate;
      if (g) setGate(g);
      setMsg({ bad: true, text: errMsg(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="in-card in-actions" onSubmit={save}>
      <h3>{mine ? 'Your annotation' : 'Annotate this turn'}</h3>
      {msg && <div className={`in-banner ${msg.bad ? 'bad' : 'info'}`}>{msg.text}</div>}
      <div className="in-field">
        Verdict
        <div>
          {vocab.verdicts.map((v) => (
            <label key={v} className="in-radio"><input type="radio" name="verdict" value={v} checked={verdict === v} onChange={() => setVerdict(v)} />{v}</label>
          ))}
        </div>
      </div>
      <div className="in-field">
        Labels
        <div className="in-chips">
          {vocab.labels.map((l) => <button key={l} type="button" className={`in-chip ${labels.includes(l) ? 'on' : ''}`} onClick={() => toggleLabel(l)}>{l.replace(/_/g, ' ')}</button>)}
        </div>
      </div>
      <div className="in-field">
        Corrected answer, in placeholder form: connective text only, every fact as a placeholder from this turn. No typed numbers, dates, amounts, places or outcome words.
        <div className="in-row">
          <select className="in-select" value={ref} onChange={(e) => setRef(e.target.value)} aria-label="Fact">
            {facts.map((f) => <option key={f.ref} value={f.ref}>{f.ref} · {f.kind} · {f.key}</option>)}
          </select>
          <select className="in-select" value={slot} onChange={(e) => setSlot(e.target.value)} aria-label="Slot">
            {vocab.slots.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="button" className="in-btn small" disabled={!ref} onClick={insert}>Insert placeholder</button>
          {checking && <span className="in-muted">checking…</span>}
        </div>
        <textarea ref={area} className="in-textarea in-mono" value={corrected} onChange={(e) => setCorrected(e.target.value)} maxLength={4000}
          placeholder="e.g. Based on what you told me, {{f:r1.f1.outcome}}. This holds while {{f:r1.f1.cond.c1}}." />
        <GateResult gate={gate} />
      </div>
      <label className="in-field">
        KB gap (opens a task for the KB factory)
        <textarea className="in-textarea" value={kbGap} onChange={(e) => setKbGap(e.target.value)} maxLength={2000} placeholder="Which rule is missing or wrong, and where the law says so" />
      </label>
      <label className="in-radio" title={canEval ? '' : 'Only a member turn with training consent can be an eval candidate'}>
        <input type="checkbox" checked={canEval && evalCandidate} disabled={!canEval} onChange={(e) => setEvalCandidate(e.target.checked)} />
        Eval candidate{canEval ? '' : ' (members with training consent only)'}
      </label>
      <div className="in-row" style={{ marginTop: 10 }}>
        <button type="submit" className="in-btn primary" disabled={busy || (gate && !gate.ok)}>Save annotation</button>
        {gate && !gate.ok && <span className="in-muted">Fix the corrected answer, or clear it, to save.</span>}
      </div>
    </form>
  );
}

export default function IntelligenceConciergeTurn() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await conciergeReviewAPI.turn(id);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [id]);
  useEffect(() => { setData(null); setFlash(null); load(); }, [load]);

  const t = data && data.turn;
  const facts = useMemo(() => (t && Array.isArray(t.facts) ? t.facts : []), [t]);
  const others = data ? data.annotations.filter((a) => !data.mine || a.id !== data.mine.id) : [];
  const g = t && t.grounding;
  const pm = t && t.plan_mode;
  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <p className="in-muted" style={{ margin: 0 }}><Link to="/intelligence/concierge">← CONCIERGE REVIEW</Link></p>
            <h1 className="page-title">TURN {id}</h1>
            {t && (
              <div className="in-row">
                <span className={`in-badge ${t.status === 'done' ? '' : 'bad'}`}>{t.kind || t.status}</span>
                <GroundingBadge g={g} />
                <span className="in-badge">{t.viewer_class}</span>
                {Number(t.training_consent) === 1 && <span className="in-badge ok">training consent</span>}
                {data.exportable ? <span className="in-badge ok">exportable once annotated</span> : <span className="in-badge">not exportable</span>}
                <span className="in-muted">{t.member} · session {t.session_id} · turn {t.seq} · {fmtDate(t.created_at)}</span>
              </div>
            )}
          </div>
        </div>
        {error && <div className="in-banner bad">{error}</div>}
        {!data ? (!error && <div className="in-empty">Loading the turn…</div>) : (
          <>
            <div className="in-grid2">
              <div className="in-card">
                <h3>Question (redacted)</h3>
                <div className="in-answer">{t.question_redacted}</div>
                <h3 style={{ marginTop: 14 }}>Answer (rendered, redacted)</h3>
                <Answer text={t.answer_redacted} spans={t.spans} />
                {t.error_code && <div className="in-banner bad" style={{ marginTop: 8 }}>Error: {t.error_code}</div>}
              </div>
              <div className="in-card">
                <h3>Grounding</h3>
                {!g ? <p className="in-muted">No report.</p> : (
                  <>
                    <p className="in-muted">held {g.held} · flagged {g.flagged} · dropped {g.dropped} · omission appended {g.omissionAppended}</p>
                    {(g.violations || []).length > 0 && <ul className="in-violations">{g.violations.map((v, i) => <li key={i}><b>{v.check}</b> sentence {String(v.sentence_hash || '').slice(0, 10)}</li>)}</ul>}
                  </>
                )}
                <h3 style={{ marginTop: 14 }}>Plan mode</h3>
                {!pm ? <p className="in-muted">—</p> : (
                  <>
                    <p className="in-muted">round {pm.round} · mode {pm.mode || '—'} · saved {pm.saved == null ? '—' : String(pm.saved)}</p>
                    {(pm.asked || []).length > 0 && (
                      <table className="in-table"><tbody>
                        {pm.asked.map((a) => (
                          <tr key={a.askId} className={a.changed_outcome === false ? 'in-diff-red' : ''}>
                            <td className="in-mono">{a.askId}</td><td>{a.answered ? 'answered' : 'not answered'}</td>
                            <td>changed_outcome {a.changed_outcome == null ? '—' : String(a.changed_outcome)}</td>
                          </tr>
                        ))}
                      </tbody></table>
                    )}
                  </>
                )}
                <h3 style={{ marginTop: 14 }}>Member feedback</h3>
                {!data.feedback.length ? <p className="in-muted">None.</p> : data.feedback.map((f, i) => (
                  <p key={i} className="in-muted">{Number(f.rating) < 0 ? 'thumbs down' : 'thumbs up'}{f.reason ? ` · ${f.reason}` : ''}{f.comment_redacted ? `: ${f.comment_redacted}` : ''}</p>
                ))}
              </div>
            </div>

            <div className="in-card">
              <h3>Facts the answer could use</h3>
              {!facts.length ? <p className="in-muted">No facts.</p> : (
                <div className="in-table-wrap">
                  <table className="in-table">
                    <thead><tr><th>Ref</th><th>Key</th><th>Kind</th><th>Output</th><th>Assertion</th><th>Rules</th><th>Review status</th></tr></thead>
                    <tbody>
                      {facts.map((f) => (
                        <tr key={f.ref}>
                          <td className="in-mono">{f.ref}</td><td className="in-mono">{f.key}</td><td>{f.kind}</td><td>{f.output}</td>
                          <td><span className={`in-badge ${f.assertion === 'hedged' ? 'warn' : ''}`}>{f.assertion}</span></td>
                          <td className="in-mono">{(f.ruleIds || []).join(', ') || '—'}</td>
                          <td>{f.signedOff && f.visibility === 'member' ? <span className="in-badge ok">signed, member-visible</span> : <span className="in-badge warn">{f.signedOff ? 'signed' : 'unsigned'} · {f.visibility}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="in-grid2">
              <div className="in-card">
                <h3>Tools</h3>
                {!(t.tools || []).length ? <p className="in-muted">No tool calls.</p> : (
                  <table className="in-table">
                    <thead><tr><th>Tool</th><th>ms</th><th>Result</th><th>Args (redacted)</th></tr></thead>
                    <tbody>
                      {t.tools.map((x, i) => (
                        <tr key={i} className={x.ok ? '' : 'in-diff-red'}>
                          <td className="in-mono">{x.name}</td><td>{x.ms}</td><td className="in-mono">{x.ok ? x.result : `failed: ${x.result || ''}`}</td>
                          <td><details><summary className="in-muted">args</summary><pre className="in-pre">{JSON.stringify(x.args_redacted, null, 2)}</pre></details></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="in-card">
                <h3>Versions and cost</h3>
                <table className="in-table"><tbody>
                  <tr><td>Models</td><td className="in-mono">{(t.models || []).join(', ') || '—'}</td></tr>
                  <tr><td>Engine</td><td className="in-mono">{t.engine_version || '—'}</td></tr>
                  <tr><td>KB</td><td className="in-mono">{t.kb_version || '—'}</td></tr>
                  <tr><td>Prompt</td><td className="in-mono">{String(t.prompt_hash || '').slice(0, 16)} · contract 1.{t.contract_minor}</td></tr>
                  <tr><td>Latency</td><td>{t.latency_ms != null ? `${(t.latency_ms / 1000).toFixed(1)} s` : '—'}</td></tr>
                  <tr><td>Cost</td><td>{t.usage ? `${usd(t.usage.usd)} · ${(t.usage.calls || []).length} call(s)` : '—'}</td></tr>
                </tbody></table>
                {data.session.length > 1 && (
                  <>
                    <h4>This session</h4>
                    <ul className="in-plain">
                      {data.session.map((s) => (
                        <li key={s.id}>{Number(s.id) === Number(t.id) ? <b>#{s.seq} {s.question}</b> : <Link to={`/intelligence/concierge/turns/${s.id}`}>#{s.seq} {s.question}</Link>}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>

            {others.length > 0 && (
              <div className="in-card">
                <h3>Other annotations</h3>
                {others.map((a) => (
                  <div key={a.id} style={{ marginBottom: 10 }}>
                    <div className="in-row">
                      <span className={`in-badge ${a.verdict}`}>{a.verdict}</span>
                      <span className="in-muted">{a.admin_name || a.admin_email || `admin ${a.admin_id}`} · {fmtDate(a.updated_at || a.created_at)}</span>
                      {(a.labels || []).map((l) => <span key={l} className="in-badge">{l}</span>)}
                      {Number(a.eval_candidate) ? <span className="in-badge ok">eval candidate</span> : null}
                    </div>
                    {a.corrected_answer && <pre className="in-pre" style={{ marginTop: 6 }}>{a.corrected_answer}</pre>}
                    {a.kb_gap_note && <p className="in-muted">KB gap: {a.kb_gap_note}{a.kbf_task_id ? ` (task ${a.kbf_task_id})` : ''}</p>}
                  </div>
                ))}
              </div>
            )}

            {flash && <div className="in-banner info">{flash} <Link to="/intelligence/kb?tab=tasks">KB tasks</Link></div>}
            <AnnotationForm key={data.mine ? `${data.mine.id}-${data.mine.updated_at}` : 'new'} turn={t} facts={facts} vocab={data.vocab} mine={data.mine}
              onSaved={(text) => { setFlash(text); load(); }} />
          </>
        )}
      </main>
    </div>
  );
}
